import { LDFlagSet } from 'launchdarkly-js-sdk-common';
import { useMemo } from 'react';

type FacilityUiVisibility = {
  fundingRequest: {
    showVerificationReportIfAvailable: boolean; // show verification report if available in the individual funding request page
  };
  verification: {
    showAssetVerification: boolean; // show results from asset verification
    showAssetVerificationReportUpload: boolean; // show 'Upload New Report' button
    showAdjudicationActions: boolean; // show ability to take verification adjudication actions
    showNotifyReportReadyAction: boolean; // show ability to send notification that verification report is ready for review
    showRequestRemediationAction: boolean; // show request remediation button
    showWaiveReceivableEligibilityActions: boolean; // show ability to take receivable eligibility waiver actions
    showConcentrationLimitsInsightsLink: boolean; // show link to concentration limit insights on the verification report
  };
};

// type FacilityUiVisibilities = {
//   creditFacilityId: string;
//   uiVisibility: FacilityUiVisibility;
// }[];

type OrganizationUiVisibility = {
  sideBarItems: {
    home: boolean;
    creditAgreements: boolean;
    reports: boolean;
    manualUploader: boolean;
    bbplMonitor: boolean;
    assignments: boolean;
    receivables: boolean;
    fundingRequests: boolean;
    strats: boolean;
    insights: boolean;
    statements: boolean;
    deliverables: boolean;
    resources: boolean;
    events: boolean;
    settings: boolean;
  };
  fundingRequest: {
    showFundingRequestWorkflow: boolean;
  };
  verification: {
    showVerificationPipelines: boolean;
    showInitiateVerification: boolean;
  };
  defaultRoute: string;
};

const mockBorrowerOwnerFacilityUiVisibility: FacilityUiVisibility = {
  fundingRequest: {
    showVerificationReportIfAvailable: false,
  },
  verification: {
    showAssetVerification: false,
    showAssetVerificationReportUpload: false,
    showAdjudicationActions: false,
    showNotifyReportReadyAction: false,
    showRequestRemediationAction: false,
    showWaiveReceivableEligibilityActions: false,
    showConcentrationLimitsInsightsLink: false,
  },
};

const mockBorrowerLimitedFacilityUiVisibility: FacilityUiVisibility = {
  fundingRequest: {
    showVerificationReportIfAvailable: true,
  },
  verification: {
    showAssetVerification: true,
    showAssetVerificationReportUpload: false, // Currently hiding for borrowers.
    showAdjudicationActions: false,
    showNotifyReportReadyAction: true,
    showRequestRemediationAction: false,
    showWaiveReceivableEligibilityActions: true,
    showConcentrationLimitsInsightsLink: false,
  },
};

const mockLenderOwnerWithBorrowerLimitedFacilityUiVisibility: FacilityUiVisibility = {
  fundingRequest: {
    showVerificationReportIfAvailable: true,
  },
  verification: {
    showAssetVerification: true,
    // Won't work yet for all facilities, but currently the only one that will show a non-working
    // upload button is presidio, a demo account.
    showAssetVerificationReportUpload: true,
    showAdjudicationActions: true,
    showNotifyReportReadyAction: false,
    showRequestRemediationAction: true,
    showWaiveReceivableEligibilityActions: true,
    showConcentrationLimitsInsightsLink: true,
  },
};

const mockLenderOwnerWithoutBorrowerFacilityUiVisibility: FacilityUiVisibility = {
  fundingRequest: {
    showVerificationReportIfAvailable: true,
  },
  verification: {
    showAssetVerification: true,
    showAssetVerificationReportUpload: false, // code does not correctly handle all facilities yet.
    showAdjudicationActions: true,
    showNotifyReportReadyAction: false,
    showRequestRemediationAction: false,
    showWaiveReceivableEligibilityActions: true,
    showConcentrationLimitsInsightsLink: true,
  },
};

// const mockCreditFacilityId = 'mockCreditFacilityId';
// const mockCreditFacilityId2 = 'mockCreditFacilityId2';
// const mockCreditFacilityId3 = 'mockCreditFacilityId3';
// const mockFacilityUiVisibilities: FacilityUiVisibilities = [
//   {
//     creditFacilityId: mockCreditFacilityId,
//     uiVisibility: mockBorrowerOwnerFacilityUiVisibility,
//   },
//   {
//     creditFacilityId: mockCreditFacilityId2,
//     uiVisibility: mockFacilityUiVisibility,
//   },
//   {
//     creditFacilityId: mockCreditFacilityId3,
//     uiVisibility: mockFacilityUiVisibility,
//   },
// ];

type MockUiVisibilityMode =
  | 'lenderOwnerWithBorrowerLimited'
  | 'lenderOwnerWithoutBorrower'
  | 'borrowerOwner'
  | 'borrowerLimited';

const mockBorrowerOwnerOrganizationUiVisibility: OrganizationUiVisibility = {
  sideBarItems: {
    home: true,
    creditAgreements: true,
    reports: true,
    manualUploader: true,
    bbplMonitor: true,
    assignments: true,
    receivables: true,
    fundingRequests: true,
    strats: true,
    insights: true,
    statements: true,
    deliverables: true,
    resources: true,
    events: true,
    settings: true,
  },
  fundingRequest: {
    showFundingRequestWorkflow: true,
  },
  verification: {
    showVerificationPipelines: false,
    showInitiateVerification: false,
  },
  defaultRoute: '/',
};

const mockBorrowerLimitedOrganizationUiVisibility: OrganizationUiVisibility = {
  sideBarItems: {
    home: false,
    creditAgreements: false,
    reports: false,
    manualUploader: false,
    bbplMonitor: false,
    receivables: false,
    assignments: false,
    fundingRequests: true,
    strats: false,
    insights: false,
    statements: false,
    deliverables: false,
    resources: true,
    events: false,
    settings: false,
  },
  fundingRequest: {
    showFundingRequestWorkflow: false,
  },
  verification: {
    showVerificationPipelines: true,
    showInitiateVerification: true,
  },
  defaultRoute: '/funding-requests',
};

const mockLenderOwnerWithBorrowerLimitedOrganizationUiVisibility: OrganizationUiVisibility = {
  sideBarItems: {
    home: true,
    creditAgreements: true,
    reports: true,
    manualUploader: false,
    bbplMonitor: false,
    receivables: false,
    assignments: false,
    fundingRequests: true,
    strats: false,
    insights: false,
    statements: false,
    deliverables: true,
    resources: true,
    events: true,
    settings: true,
  },
  fundingRequest: {
    showFundingRequestWorkflow: false,
  },
  verification: {
    showVerificationPipelines: true,
    showInitiateVerification: true,
  },
  defaultRoute: '/',
};

const mockLenderOwnerWithoutBorrowerOrganizationUiVisibility = mockLenderOwnerWithBorrowerLimitedOrganizationUiVisibility;

const mockFacilityUiVisibilityToModeMap: {
  [key in MockUiVisibilityMode]: FacilityUiVisibility;
} = {
  lenderOwnerWithBorrowerLimited: mockLenderOwnerWithBorrowerLimitedFacilityUiVisibility,
  lenderOwnerWithoutBorrower: mockLenderOwnerWithoutBorrowerFacilityUiVisibility,
  borrowerOwner: mockBorrowerOwnerFacilityUiVisibility,
  borrowerLimited: mockBorrowerLimitedFacilityUiVisibility,
};

const mockOrganizationUiVisibilityToModeMap: {
  [key in MockUiVisibilityMode]: OrganizationUiVisibility;
} = {
  lenderOwnerWithBorrowerLimited: mockLenderOwnerWithBorrowerLimitedOrganizationUiVisibility,
  lenderOwnerWithoutBorrower: mockLenderOwnerWithoutBorrowerOrganizationUiVisibility,
  borrowerOwner: mockBorrowerOwnerOrganizationUiVisibility,
  borrowerLimited: mockBorrowerLimitedOrganizationUiVisibility,
};

// Note this is just temporary to help drive demo purpose visibility.
const mockReportingOnlyOrgUiVisibility: OrganizationUiVisibility = {
  sideBarItems: {
    home: false,
    creditAgreements: false,
    reports: false,
    manualUploader: false,
    bbplMonitor: false,
    receivables: false,
    assignments: false,
    fundingRequests: false,
    strats: true,
    insights: false,
    statements: false,
    deliverables: false,
    resources: false,
    events: false,
    settings: false,
  },
  fundingRequest: {
    showFundingRequestWorkflow: false,
  },
  verification: {
    showVerificationPipelines: false,
    showInitiateVerification: false,
  },
  defaultRoute: '/strats',
};

const mockOrgSlugToUiVisibilityMap: {
  [orgSlug: string]: OrganizationUiVisibility;
} = {
  insuranceCo1: mockReportingOnlyOrgUiVisibility,
  insuranceCo2: mockReportingOnlyOrgUiVisibility,
};

export const getMockUiVisibilityMode = ({
  flags,
  defaultTo,
}: {
  flags: LDFlagSet | undefined;
  defaultTo: MockUiVisibilityMode;
}): MockUiVisibilityMode => {
  if (!flags) {
    return defaultTo;
  }
  if (flags.lenderOwnerOrganizationWithBorrowerLimited) {
    return 'lenderOwnerWithBorrowerLimited';
  }
  if (flags.isBorrowerLimitedOrganization) {
    return 'borrowerLimited';
  }
  return defaultTo;
};

// @JonAtFinley @danitian replace with real UiVisibility returned from auth
export const useCreditFacilityUiVisibility = ({
  mockUiVisibilityMode,
}: {
  mockUiVisibilityMode: MockUiVisibilityMode;
  creditFacilityId: string;
}) => {
  return useMemo(
    () => mockFacilityUiVisibilityToModeMap[mockUiVisibilityMode],
    [mockUiVisibilityMode],
  );
};

// @JonAtFinley @danitian replace with real UiVisibility returned from auth
export const useOrganizationUiVisibility = ({
  mockUiVisibilityMode,
  organizationSlug,
}: {
  mockUiVisibilityMode: MockUiVisibilityMode;
  organizationSlug?: string;
}): OrganizationUiVisibility => {
  return useMemo(() => {
    if (organizationSlug && mockOrgSlugToUiVisibilityMap[organizationSlug]) {
      return mockOrgSlugToUiVisibilityMap[organizationSlug];
    }
    return mockOrganizationUiVisibilityToModeMap[mockUiVisibilityMode];
  }, [mockUiVisibilityMode, organizationSlug]);
};
